import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { addProductToCart } from '@/actions/productsCart';
import { usePassengerHasProducts } from 'hooks/store/productsCart/usePassengerHasProducts';
import { useHasCatalogProducts } from 'hooks/store/productsCart/useHasCatalogProducts';
import SnacksSummaryContainer from 'ui/molecules/SnacksSummaryContainer';
import SnacksShowcaseContainer from 'ui/molecules/SnacksShowcaseContainer';
import SnacksModalContainer from 'ui/molecules/SnacksModalContainer';

/**
 * SnacksCart component.
 * Container that manages the snacks cart summary display and modal state for a specific passenger.
 * Shows SnacksShowcaseContainer when no products selected and catalog has products available.
 * Shows SnacksSummaryContainer when products exist.
 * Opens modal on "Ver detalles" click.
 *
 * @param {object} props - Component props
 * @param {number} props.passengerIndex - Index of the passenger
 * @param {boolean} props.isRoundTrip - Indicates if the trip is round trip
 * @returns {React.ReactNode} SnacksCart container or null
 */
const SnacksCart = ({ passengerIndex, isRoundTrip }) => {
  // When isRoundTrip, check if catalog has products in either segment
  const segmentToCheck = isRoundTrip ? 'both' : 'outgoing';
  const hasProducts = usePassengerHasProducts(passengerIndex, segmentToCheck);
  const hasCatalogProducts = useHasCatalogProducts(segmentToCheck);
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Don't render anything if catalog doesn't have products
  if (!hasCatalogProducts) {
    return null;
  }

  /**
   * Handle opening the modal
   */
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  /**
   * Handle closing the modal
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  /**
   * Handle adding a product to cart from showcase
   * @param {object} product - Product to add
   */
  const handleAddProduct = (product) => {
    // Determine segment based on product's available segments
    // If available in both, default to 'outgoing'
    const { availableInSegment, ...restProduct } = product;
    const segment = availableInSegment === 'both' ? 'outgoing' : availableInSegment || 'outgoing';
    dispatch(addProductToCart({ product: restProduct, quantity: 1, passengerIndex, segment }));
    // Open modal after adding product from showcase
    setIsModalOpen(true);
  };

  /**
   * Handle viewing all products in modal
   */
  const handleViewAll = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      {!hasProducts ? (
        <SnacksShowcaseContainer
          isRoundTrip={isRoundTrip}
          onViewAll={handleViewAll}
          onAddProduct={handleAddProduct}
        />
      ) : (
        <SnacksSummaryContainer
          passengerIndex={passengerIndex}
          isRoundTrip={isRoundTrip}
          onViewDetails={handleOpenModal}
        />
      )}

      <SnacksModalContainer
        passengerIndex={passengerIndex}
        isRoundTrip={isRoundTrip}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

SnacksCart.propTypes = {
  passengerIndex: PropTypes.number.isRequired,
  isRoundTrip: PropTypes.bool.isRequired,
};

export default SnacksCart;
